import React from 'react';
import { BarChart, Trash2, RefreshCw } from 'lucide-react';
import { formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для отображения сохраненных результатов
 */
const SavedResultsSidebar = ({ 
  savedResults, 
  deleteSavedResult, 
  loadSavedResult 
}) => {
  // Если нет сохраненных результатов
  if (!savedResults || savedResults.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-emerald-500" />
          История измерений
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные результаты измерений для отслеживания прогресса.
        </p>
      </div>
    );
  }

  // Сортировка результатов по дате (от новых к старым)
  const sortedResults = [...savedResults].sort((a, b) => 
    new Date(b.date).getTime() - new Date(a.date).getTime()
  );
  
  // Функция для получения класса цвета на основе категории WHR
  const getCategoryColorClass = (categoryId) => {
    if (categoryId.includes('normal')) return 'text-emerald-600 dark:text-emerald-400';
    if (categoryId.includes('low')) return 'text-amber-600 dark:text-amber-400';
    return 'text-red-600 dark:text-red-400';
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-emerald-500" />
          История измерений
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedResults.length} измер.
        </span>
      </div>

      {savedResults.length > 1 && (
        <div className="p-3 mb-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 text-xs text-blue-700 dark:text-blue-300">
          <p>
            Отслеживайте изменения вашего WHR с течением времени для более эффективного контроля состояния здоровья.
          </p>
        </div>
      )}

      <div className="space-y-3">
        {sortedResults.map((result) => (
          <div 
            key={result.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {result.name}
              </h4>
              <div className="flex items-center space-x-1">
                <Button
                  size="xs"
                  variant="ghost"
                  icon={<RefreshCw className="w-4 h-4" />}
                  onClick={() => loadSavedResult(result.id)}
                  title="Загрузить результат"
                />
                <Button
                  size="xs"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedResult(result.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              {new Date(result.date).toLocaleDateString('ru-RU', { 
                day: 'numeric', 
                month: 'long', 
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex flex-col">
                <span className="text-xs text-gray-500 dark:text-gray-400">WHR</span>
                <span className={`font-medium ${getCategoryColorClass(result.results.category.id)}`}>
                  {formatNumber(result.results.whr, 2)}
                </span>
              </div>
              <div className="flex flex-col">
                <span className="text-xs text-gray-500 dark:text-gray-400">Категория</span>
                <span className={`font-medium ${getCategoryColorClass(result.results.category.id)}`}>
                  {result.results.category.name}
                </span>
              </div>
              <div className="flex flex-col">
                <span className="text-xs text-gray-500 dark:text-gray-400">Талия</span>
                <span className="text-gray-700 dark:text-gray-300">
                  {result.results.measurements.waist} {result.results.measurements.measurementSystem === 'metric' ? 'см' : 'дюйм'}
                </span>
              </div>
              <div className="flex flex-col">
                <span className="text-xs text-gray-500 dark:text-gray-400">Бедра</span>
                <span className="text-gray-700 dark:text-gray-300">
                  {result.results.measurements.hip} {result.results.measurements.measurementSystem === 'metric' ? 'см' : 'дюйм'}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Дополнительная информация */}
      {savedResults.length > 0 && (
        <div className="mt-4">
          <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Сохраненные результаты хранятся локально в вашем браузере и не передаются на сервер.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SavedResultsSidebar;